import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import { IEquipment } from '../../types';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { EquipmentLinkMode } from '../../types';
import ConfirmationUnlinkButtonDialog from 'components/ui/feedback/ConfirmationButtonUnlink';
import { SectionHeader } from 'components/ui/typography';

/**
 * Maps equipment relational cost fields to their corresponding navigation routes.
 * Each entry links an `IEquipment` key to its detail route path.
 */
const COST_LINKS = [
  { field: 'laboratories', link: '/tenant/topology/lab/' },
  { field: 'operationalunits', link: '/tenant/topology/operationalunit/' },
  { field: 'sectors', link: '/tenant/topology/sector/' },
  { field: 'workstations', link: '/tenant/cost-analysis/workstations/' },
  { field: 'exams', link: '/tenant/cost-analysis/exams/' }
] as const;

/**
 * Props for the `CostAnalysTab` component.
 */
interface CostAnalysTabProps {
  equipment: IEquipment;
  isDisabledEdit?: boolean;
  setMode: (field: EquipmentLinkMode) => void;
  /**
   * Unlinks an entity from the equipment.
   * @param id - Entity identifier.
   * @param field - Model name / relational key (e.g. 'laboratories').
   */
  handleRemoveLink: (id: number, field: string) => void;
}

/**
 * Cost analysis tab for a piece of equipment.
 *
 * Displays up to 5 columns of linked entities (laboratories, operational units,
 * sectors, workstations, exams). Each column lists items with code and
 * description, and provides actions to view details or unlink the entity.
 * Includes a button to add new links when `isDisabledEdit` is `false`.
 */
export const CostAnalysTab = ({
  equipment,
  isDisabledEdit,
  setMode,
  handleRemoveLink
}: CostAnalysTabProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <SectionHeader
        title={t('global.costs')}
        subtitle="Configura le relazioni e i flussi di costo associati a questo strumento."
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
        {COST_LINKS.map((config, idx) => {
          const items =
            (equipment[config.field as keyof IEquipment] as Array<{
              id: number;
              code: string;
              description?: string;
            }>) ?? [];
          const isLast = idx === COST_LINKS.length - 1;

          return (
            <Box
              key={config.field}
              sx={{
                width: { xs: '100%', sm: '50%', lg: '20%' },
                pl: idx === 0 ? 0 : 2,
                pr: isLast ? 0 : 2,
                borderRight: isLast ? 0 : 1,
                borderColor: 'divider'
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="body1" fontWeight={700}>
                  {t(`global.${config.field}`)}
                </Typography>
                {!isDisabledEdit && (
                  <IconButton
                    color="primary"
                    onClick={() => setMode(config.field as EquipmentLinkMode)}
                    size="small">
                    <AddCircleOutlineIcon />
                  </IconButton>
                )}
              </Box>

              <List dense>
                {items.map((entity) => (
                  <Fragment key={entity.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          pr: 2 // padding standard destro; le icone sono dentro il flusso
                        }}>
                        {/* Testo: cresce e si restringe, mai sovrapposto */}
                        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                          <ListItemText
                            primary={entity.code}
                            secondary={entity.description}
                            primaryTypographyProps={{
                              noWrap: true,
                              title: entity.code,
                              fontWeight: 500
                            }}
                            secondaryTypographyProps={{
                              noWrap: true,
                              title: entity.description,
                              variant: 'body2'
                            }}
                          />
                        </Box>

                        {/* Azioni: larghezza fissa, mai compresse */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexShrink: 0,
                            alignItems: 'center',
                            gap: 1
                          }}>
                          <Tooltip title={t('actions.details')}>
                            <IconButton
                              component={NavLink}
                              to={`${config.link}${entity.id}`}
                              aria-label={t('actions.details')}
                              sx={{ color: 'text.secondary' }}
                              onClick={(e) => e.stopPropagation()}>
                              {' '}
                              {/* evita conflitti se ListItemButton ha onClick */}
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('actions.unlink')}>
                            <ConfirmationUnlinkButtonDialog
                              handleUnlink={handleRemoveLink}
                              id={entity.id}
                              pars={config.field}
                              iconButton
                            />
                          </Tooltip>
                        </Box>
                      </ListItemButton>
                    </ListItem>

                    {!isLast && <Divider component="li" />}
                  </Fragment>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
